import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Share2, Copy, Check, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Workspace "Share" button — creates a public, unauthenticated read-only link.
 * Privacy: only the text is sent. The backend stores ONLY the text + opaque share_id.
 */
export default function ShareButton({ output, disabled, onShared }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const createShare = async () => {
    if (!output) {
      toast.error("Generate a response first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/share", { text: output });
      const url = `${window.location.origin}${data.share_url_path}`;
      setShareUrl(url);
      setOpen(true);
      onShared?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied");
  };

  return (
    <>
      <Button
        data-testid="share-output-btn"
        size="sm"
        variant="outline"
        onClick={createShare}
        disabled={disabled || !output || loading}
        className="h-8 border-border bg-charcoal hover:bg-white/[0.04] text-white/80 rounded-sm"
      >
        <Share2 className="h-3.5 w-3.5 mr-1.5" />
        {loading ? "Sealing…" : "Share"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-modal" className="max-w-md bg-charcoal border-border">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-white flex items-center gap-2">
              <Share2 className="h-4 w-4 text-teal" /> Public read-only link
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Anyone with this link can read the draft. No login required. Your name, email and persona are NOT attached — only the text.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/40 mb-1.5">Share URL</div>
            <div className="flex items-stretch gap-2">
              <code
                data-testid="share-url"
                className="flex-1 bg-charcoal border border-border px-3 py-2 rounded-sm text-xs font-mono text-teal break-all"
              >
                {shareUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={copy}
                data-testid="share-copy-btn"
                className="h-auto border-border bg-charcoal text-white/70 rounded-sm"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="share-open-link"
              className="inline-flex items-center gap-1.5 mt-3 text-xs text-white/60 hover:text-white"
            >
              <ExternalLink className="h-3 w-3" /> Open in new tab
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
